import * as back from '~/components/layouts/Navigation/BreadcrumbContainer/functional/backLinks';
import { crudBreadcrumb, listBreadcrumb } from '~/components/layouts/Navigation/BreadcrumbContainer/functional/generators';
import * as match from '~/components/layouts/Navigation/BreadcrumbContainer/functional/matchers';
import { getTenantFullName } from '~/components/tenants';
import type { RouteRule, BreadcrumbContext, BreadcrumbItem } from '~/components/layouts/Navigation/BreadcrumbContainer/functional/types';

const PRIORITY = {
  SECTION_BASE: 360,
  LIST_PAGE: 370,
  ADD_FORM: 400,
  EDIT_FORM: 420,
  DETAIL_PAGE: 430,
  PAYMENT_METHODS: 440,
  TAX_DOCUMENTS: 450,
  GENERATE_TAX_DOCS: 460,
} as const;

const tenantDetailBreadcrumb = (action?: string) => (context: BreadcrumbContext) => {
  const tenantId = context.path.match(/\/users\/tenants\/(\d+)/)?.[1];
  const breadcrumbs: BreadcrumbItem[] = [
    { label: context.t('Users') },
    { label: context.t('Tenants'), link: '/users/tenants' },
    { label: getTenantFullName(context.currentTenant()) || context.t('Loading'), link: `/users/tenants/${tenantId}` },
  ];

  if (action) {
    breadcrumbs.push({ label: context.t(action) });
  }

  return breadcrumbs;
};

const ownerDetailBreadcrumb = (action?: string) => (context: BreadcrumbContext) => {
  const ownerId = context.path.match(/\/users\/owners\/(\d+)/)?.[1];
  const breadcrumbs: BreadcrumbItem[] = [
    { label: context.t('Users') },
    { label: context.t('Owners'), link: '/users/owners' },
    { label: getTenantFullName(context.currentOwner()) || context.t('Loading'), link: `/users/owners/${ownerId}` },
  ];

  if (action) {
    breadcrumbs.push({ label: context.t(action) });
  }

  return breadcrumbs;
};

const propertyManagerDetailBreadcrumb = (action?: string) => (context: BreadcrumbContext) => {
  const managerId = context.path.match(/\/users\/property-managers\/(\d+)/)?.[1];
  const manager = context.currentPropertyManager();
  const managerName = manager?.name || context.t('Loading');
  const breadcrumbs: BreadcrumbItem[] = [
    { label: context.t('Users') },
    { label: context.t('Property managers'), link: '/users/property-managers' },
    { label: managerName, link: `/users/property-managers/${managerId}` },
  ];

  if (action) {
    breadcrumbs.push({ label: context.t(action) });
  }

  return breadcrumbs;
};

export const userRules: RouteRule[] = [
  {
    match: match.and(match.startsWith('/users/tenants'), match.hasId(), match.includes('/payments/methods')),
    generate: tenantDetailBreadcrumb('Payment method'),
    backLink: back.goBack,
    priority: PRIORITY.PAYMENT_METHODS,
  },
  {
    match: match.and(match.startsWith('/users/tenants'), match.hasId(), match.includes('/edit')),
    generate: tenantDetailBreadcrumb('Edit tenant'),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/users/tenants'), match.hasId()),
    generate: tenantDetailBreadcrumb(),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/users/tenants'), match.includes('/add')),
    generate: crudBreadcrumb('Users', '/users', 'Tenants', '/users/tenants', 'Add a new tenant'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/users'), match.includes('/tenants')),
    generate: listBreadcrumb('Users', '/users', 'Tenants', '/users/tenants'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE + 50,
  },
  {
    match: match.and(match.startsWith('/users/owners'), match.hasId(), match.includes('/payments/methods')),
    generate: ownerDetailBreadcrumb('Payment method'),
    backLink: back.goBack,
    priority: PRIORITY.PAYMENT_METHODS,
  },
  {
    match: match.and(match.startsWith('/users/owners'), match.hasId(), match.includes('/edit')),
    generate: ownerDetailBreadcrumb('Edit owner'),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/users/owners'), match.hasId()),
    generate: ownerDetailBreadcrumb(),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/users/owners'), match.includes('/add')),
    generate: crudBreadcrumb('Users', '/users', 'Owners', '/users/owners', 'Add a new owner'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/users/owners/tax-documents'), match.includes('/generate')),
    generate: (context) => [
      { label: context.t('Users') },
      { label: context.t('Owners'), link: '/users/owners' },
      { label: context.t('Tax documents'), link: '/users/owners/tax-documents' },
      { label: context.t('Generate tax documents') },
    ],
    backLink: back.goBack,
    priority: PRIORITY.GENERATE_TAX_DOCS,
  },
  {
    match: match.and(match.startsWith('/users/owners'), match.includes('/tax-documents')),
    generate: (context) => [
      { label: context.t('Users') },
      { label: context.t('Owners'), link: '/users/owners' },
      { label: context.t('Tax documents'), link: '/users/owners/tax-documents' },
    ],
    backLink: back.goBack,
    priority: PRIORITY.TAX_DOCUMENTS,
  },
  {
    match: match.and(match.startsWith('/users'), match.includes('/owners')),
    generate: listBreadcrumb('Users', '/users', 'Owners', '/users/owners'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE + 40,
  },
  {
    match: match.and(match.startsWith('/users/property-managers'), match.hasId(), match.includes('/edit')),
    generate: propertyManagerDetailBreadcrumb('Edit property manager'),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/users/property-managers'), match.hasId()),
    generate: propertyManagerDetailBreadcrumb(),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/users/property-managers'), match.includes('/add')),
    generate: crudBreadcrumb('Users', '/users', 'Property managers', '/users/property-managers', 'Add a new property manager'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/users'), match.includes('/property-managers')),
    generate: listBreadcrumb('Users', '/users', 'Property managers', '/users/property-managers'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE + 30,
  },
  {
    match: match.startsWith('/users'),
    generate: (context) => [{ label: context.t('Users') }],
    backLink: back.homeLink,
    priority: PRIORITY.SECTION_BASE,
  },
];
